import {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "../hooks/useAuth.js";
import { useContent } from "../hooks/useContent.js";
import { api } from "../lib/api.js";
import type { ContentFormat } from "../lib/types.js";

/**
 * Both of these are large and conditionally needed, so neither is imported
 * eagerly.
 *
 * `RichTextEditor` pulls in TipTap (~125 KB gzipped) and can only ever be
 * opened by Davis. `RichText` pulls in the Markdown renderer (~100 KB gzipped)
 * and is only needed for a block that has actually been overridden. Importing
 * either at the top of this file would put it in the chunk for every page that
 * has an editable string on it — which, by design, is all of them — and hand a
 * quarter of a megabyte to readers who can use none of it.
 */
const RichText = lazy(() =>
  import("./RichText.js").then((m) => ({ default: m.RichText })),
);
const RichTextEditor = lazy(() =>
  import("./RichTextEditor.js").then((m) => ({ default: m.RichTextEditor })),
);

/**
 * In-place editing of the site's own copy.
 *
 * The rule this is built on: **the page owns the default**. Every editable
 * string is written into the JSX exactly as it always was, and a stored
 * override replaces it. That ordering is what lets the site render correctly
 * with an empty database, with the API down, and on the very first paint before
 * the content request has come back — and it means clearing a block is a real
 * "reset to how it shipped" rather than a way to blank the page.
 *
 * For a signed-out reader this renders the text and nothing else: no wrapper,
 * no hover target, no extra bytes. The editing chrome only exists for an admin.
 * That check is a convenience, not a boundary — `PATCH /api/content/:key`
 * re-checks the role server-side, so editing `role` in devtools changes what is
 * drawn and nothing about what saves.
 */

interface EditableProps {
  /** Dotted block id, namespaced by page: `about.intro`. */
  id: string;
  /** Where the pencil sits. Defaults to the top-right of the block. */
  pencil?: "top-right" | "top-left" | "bottom-right";
  className?: string;
}

/**
 * A run of plain text — a heading, a label, a button caption, a tagline.
 *
 * `children` is the default, and it is text rather than JSX on purpose: the
 * stored override is plain text, so a default containing markup could not be
 * round-tripped through the editor without silently dropping it. Use
 * `EditableRich` where the copy needs links.
 *
 * The array form is what JSX produces when the default interpolates a constant
 * — `Real reactions from {site.author} —` arrives as three pieces. Joining them
 * means those stay interpolated rather than having to be flattened into
 * literals, so `site.ts` remains the one place a name or a year is written.
 */
export function EditableText({
  id,
  children,
  pencil = "top-right",
  className,
}: EditableProps & { children: string | (string | number)[] }) {
  const { blocks } = useContent();
  const { isAdmin } = useAuth();

  const fallback = Array.isArray(children) ? children.join("") : children;
  const value = blocks[id]?.value ?? fallback;

  if (!isAdmin) return <>{value}</>;

  return (
    <EditableShell
      id={id}
      format="text"
      current={blocks[id]?.value ?? ""}
      fallback={fallback}
      pencil={pencil}
      className={className}
      display="inline"
    >
      {value}
    </EditableShell>
  );
}

/**
 * A block of rich copy — paragraphs, links, the odd image.
 *
 * `children` here *is* JSX, because the default can be as rich as the page
 * needs (the About intro carries two react-router `<Link>`s). Once overridden,
 * the stored HTML renders through the same sanitiser as review bodies.
 */
export function EditableRich({
  id,
  children,
  pencil = "top-right",
  className,
}: EditableProps & { children: ReactNode }) {
  const { blocks } = useContent();
  const { isAdmin } = useAuth();

  const stored = blocks[id]?.value;

  // The page's own copy stands in while the renderer loads, so an overridden
  // block shows the original words for a frame rather than a blank gap.
  const body = stored ? (
    <Suspense fallback={children}>
      <RichText>{stored}</RichText>
    </Suspense>
  ) : (
    children
  );

  if (!isAdmin) return <>{body}</>;

  return (
    <EditableShell
      id={id}
      format="html"
      current={stored ?? ""}
      fallback=""
      pencil={pencil}
      className={className}
      display="block"
    >
      {body}
    </EditableShell>
  );
}

interface ShellProps {
  id: string;
  format: ContentFormat;
  /** The stored override, or "" when the page default is showing. */
  current: string;
  /** Seeds the editor the first time a text block is edited. */
  fallback: string;
  pencil: "top-right" | "top-left" | "bottom-right";
  className: string | undefined;
  display: "inline" | "block";
  children: ReactNode;
}

const PENCIL_POSITION = {
  "top-right": "-top-2 -right-2",
  "top-left": "-top-2 -left-2",
  "bottom-right": "-bottom-2 -right-2",
} as const;

/** The hover affordance, the editor, and the save/reset plumbing behind both. */
function EditableShell({
  id,
  format,
  current,
  fallback,
  pencil,
  className,
  display,
  children,
}: ShellProps) {
  const { save } = useContent();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rendered = useRef<HTMLSpanElement>(null);

  function open() {
    // Always seed the editor with the words already on screen. Opening an empty
    // box would mean the first edit to any block is a retype from scratch, and
    // for the rich blocks — two paragraphs of Davis's own writing — that is the
    // difference between a usable editor and a destructive one.
    //
    // A rich block's default is JSX, so there is no string to fall back to;
    // the markup is read out of the DOM node that is already rendering it.
    // `display: contents` on that node means it wraps the children without
    // adding a box, so what comes back is exactly the block's own markup.
    const seed =
      format === "html"
        ? (rendered.current?.innerHTML ?? "")
        : fallback;

    setDraft(current || seed);
    setError(null);
    setEditing(true);
  }

  async function commit(next: string) {
    setBusy(true);
    setError(null);

    try {
      await save(id, next, format);
      setEditing(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "That could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  const Wrapper = display === "inline" ? "span" : "div";

  if (editing) {
    return (
      <Wrapper className={`block ${className ?? ""}`}>
        <EditorPanel
          id={id}
          format={format}
          draft={draft}
          onDraft={setDraft}
          busy={busy}
          error={error}
          hasOverride={current !== ""}
          display={display}
          onCancel={() => setEditing(false)}
          onSave={() => void commit(draft)}
          onReset={() => void commit("")}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper
      className={`group/edit relative ${
        display === "inline" ? "inline-block" : "block"
      } ${className ?? ""}`}
    >
      {/*
        `contents` adds no box of its own, so this span is invisible to layout
        while still giving `open()` a node to read the block's own markup from.
      */}
      <span ref={rendered} className="contents">
        {children}
      </span>

      {/*
        Hidden until hover or keyboard focus, and `focus-within` is not optional
        here — without it the button is unreachable by keyboard, since it would
        vanish the moment it took focus.
      */}
      <button
        type="button"
        onClick={open}
        title="Edit this"
        aria-label={`Edit ${id}`}
        className={`absolute ${PENCIL_POSITION[pencil]} z-20 grid h-7 w-7 place-items-center rounded-full border border-lamp-400/60 bg-ink-900/95 text-xs text-lamp-400 opacity-0 shadow-lg transition-opacity duration-150 group-hover/edit:opacity-100 focus-visible:opacity-100 hover:bg-lamp-400 hover:text-ink-900`}
      >
        <span aria-hidden="true">✎</span>
      </button>

      {/* A dotted hairline on hover, so it is obvious what the pencil edits. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-1.5 rounded-sm border border-dashed border-lamp-400/0 transition-colors duration-150 group-hover/edit:border-lamp-400/35"
      />
    </Wrapper>
  );
}

interface EditorPanelProps {
  id: string;
  format: ContentFormat;
  draft: string;
  onDraft: (value: string) => void;
  busy: boolean;
  error: string | null;
  hasOverride: boolean;
  /** Decides whether the panel is built from spans or divs. See below. */
  display: "inline" | "block";
  onCancel: () => void;
  onSave: () => void;
  onReset: () => void;
}

function EditorPanel({
  id,
  format,
  draft,
  onDraft,
  busy,
  error,
  hasOverride,
  display,
  onCancel,
  onSave,
  onReset,
}: EditorPanelProps) {
  const textarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textarea.current?.focus();
    textarea.current?.select();
  }, []);

  /*
    The panel's own element follows the block it replaced.

    A text block is usually *inside* a paragraph — the home page tagline is
    `<p><EditableText/></p>` — where a <div> or a nested <p> is invalid. The
    browser does not merely warn there: it closes the outer <p> early and
    reparents what follows, so the panel jumps out of its own paragraph and the
    layout tears. Inline blocks therefore build out of spans forced to `block`,
    which are phrasing content and legal anywhere.
    ↳ A rich block is never inside a paragraph, and its editor is full of
      divs — which are not legal inside a span either. So it gets real divs.
  */
  const Box = display === "inline" ? "span" : "div";
  const box = display === "inline" ? "block " : "";

  return (
    <Box
      className={`${box}relative z-30 rounded-sm border border-lamp-400/50 bg-ink-850 p-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.95)]`}
    >
      <span className="mb-2 block font-mono text-[0.65rem] uppercase tracking-[0.18em] text-paper-500">
        {id}
      </span>

      {format === "html" ? (
        <Suspense
          fallback={
            <span className="block py-8 text-center text-sm text-paper-500">
              Loading the editor…
            </span>
          }
        >
          <RichTextEditor
            value={draft}
            onChange={onDraft}
            onUploadImage={async (file) => {
              const { url } = await api.uploadSiteImage(file);
              if (!url) throw new Error("The upload did not return a URL.");
              return url;
            }}
          />
        </Suspense>
      ) : (
        <textarea
          ref={textarea}
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          rows={Math.min(8, Math.max(2, Math.ceil(draft.length / 60)))}
          // Enter saves on a single-line field; Shift+Enter still breaks the
          // line, and Escape backs out without writing anything.
          onKeyDown={(event) => {
            if (event.key === "Escape") onCancel();
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSave();
            }
          }}
          className="w-full resize-y rounded-sm border border-ink-600 bg-ink-900 px-3 py-2 text-paper-50 focus:border-lamp-400 focus:outline-none"
        />
      )}

      <span className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="rounded-sm bg-lamp-400 px-3 py-1.5 text-sm font-semibold text-ink-900 transition-colors hover:bg-lamp-300 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-sm border border-ink-600 px-3 py-1.5 text-sm text-paper-200 transition-colors hover:border-paper-400 disabled:opacity-50"
        >
          Cancel
        </button>

        {/*
          Only offered once something has actually been overridden — on an
          untouched block "reset" would delete a row that does not exist and
          look like it had done nothing.
        */}
        {hasOverride && (
          <button
            type="button"
            onClick={onReset}
            disabled={busy}
            className="ml-auto text-xs text-paper-500 underline-offset-4 transition-colors hover:text-reel-400 hover:underline disabled:opacity-50"
          >
            Reset to original
          </button>
        )}
      </span>

      {error && (
        <span className="mt-2 block text-xs text-reel-400">{error}</span>
      )}
    </Box>
  );
}
